import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { API } from "aws-amplify";
import { Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import classnames from "classnames";

import { useNotify, useRedirect, Button, Confirm } from "react-admin";
import { useForm } from "react-final-form";

import { DeleteIcon } from "../../../components/common/iconography";
import { logError } from "../../../utils/appUtils";
import {
  InboundStatus,
  InboundState,
  LocationType,
} from "../../../utils/constants";
import { deepRedirect } from "../../../utils/utils";
import { resetCapacityUsed } from "../inventory/InventoryUitls";

const useStyles = makeStyles(
  (theme) => ({
    deleteButton: {
      color: theme.palette.error.main,
      "&:hover": {
        backgroundColor: alpha(theme.palette.error.main, 0.12),
        // Reset on mouse devices
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
    },
  }),
  { name: "RaDeleteWithConfirmButton" },
);

export const DeepDeleteButton = ({
  record,
  backPath,
  className,
  isSaving,
  isDeleting,
  setIsDeleting,
  ...props
}) => {
  const form = useForm();
  const classes = useStyles();
  const history = useHistory();
  const notify = useNotify();
  const redirect = useRedirect();
  const [open, setOpen] = useState(false);

  const hasInventories = () => {
    const fieldState = form.getFieldState("inventories.items");
    return fieldState?.value?.length > 0;
  };

  const handleDialogOpen = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);

  const handleDelete = async (event) => {
    setIsDeleting(true);
    setOpen(false);
    try {
      const inventories = form.getFieldState("inventories.items")?.value;
      const photos = (form.getFieldState("photos.items")?.value || []).filter(
        (photo) => photo?.id,
      );

      const inboundDeleteInput = {
        id: record.id,
        carrierType: record.carrierType,
        attachments: record.attachments,
        receiptFile: record.receiptFile,
        photos: photos,
        timeslotId: record.timeslotId || record.timeslot?.id,
      };
      const inventoryDeleteList = [];
      const inventoryItemDeleteList = [];
      const toUpdateLocationMap = {};

      inventories?.forEach((inventory) => {
        // A row the user added in the form but never saved has no id yet.
        // Sending it puts a Key of { id: undefined } in the lambda's
        // TransactWrite, which DynamoDB rejects with a ValidationError that
        // cancels every other item with it -- so one blank line item used to
        // take the whole deep delete down. Nothing is stored for it anyway.
        if (!inventory?.id) return;
        inventoryDeleteList.push({ id: inventory.id });

        inventory.inventoryItems?.items?.forEach((inventoryItem) => {
          // Same for an unsaved item: no row to delete, and no capacity was
          // ever allocated for it, so its location must not be reset either.
          if (!inventoryItem?.id) return;
          inventoryItemDeleteList.push({
            id: inventoryItem.id,
            inventoryId: inventory.id,
          });

          if (inventoryItem.location?.type === LocationType.STRUCTURED) {
            const isOtherLoadType =
              inventoryItem.location.locationLoad?.other.selected;

            // update location capacifyUsed if STRUCTURED and not OTHER
            if (!isOtherLoadType) {
              const updatedLocation =
                toUpdateLocationMap[inventoryItem.locationId];

              if (updatedLocation) {
                // follow-on location encounters
                inventoryItem.location = updatedLocation;
                toUpdateLocationMap[inventoryItem.locationId] =
                  resetCapacityUsed(inventory, inventoryItem);
              } else {
                // first location encounter
                toUpdateLocationMap[inventoryItem.locationId] =
                  resetCapacityUsed(inventory, inventoryItem);
              }
            }
          }
        });
      });

      const locationUpdateList = Object.values(toUpdateLocationMap);

      const manualChargeDeleteList =
        record?.manualCharges?.items
          ?.filter((mc) => mc?.id)
          .map((mc) => ({ id: mc.id })) || [];
      const trackCarDeleteInput = record?.trackCar?.id
        ? { id: record.trackCar.id }
        : null;
      const response = await API.post("transREST", "/inboundDelete", {
        body: {
          inboundDeleteInput,
          inventoryDeleteList,
          inventoryItemDeleteList: inventoryItemDeleteList,
          locationUpdateList,
          manualChargeDeleteList,
          trackCarDeleteInput,
          user: window.appUser.email,
          reloadYardId: window.appUser.reloadYardId,
        },
      });
      if (response.error) throw response.error;

      notify("Inbound deleted successfully", "success");
      deepRedirect(history, backPath);
    } catch (err) {
      logError(
        "inboundShipments:DeepDeleteButton:handleDelete",
        err,
        notify,
        redirect,
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Once inbound ACCEPTED, no longer can delete.
  const isProcessed =
    !record ||
    record.status === InboundStatus.RELEASED ||
    (record.state !== InboundState.NEW &&
      record.state !== InboundState.PROCESS_SHIPMENT &&
      record.state !== InboundState.APPROVE_SHIPMENT &&
      hasInventories());

  return isProcessed ? (
    <Tooltip title="Shipment already processed." arrow placement="left">
      <span>
        {" "}
        {/* <span> is required to wrap a disabled button so tooltip still works */}
        <Button
          label="Delete"
          disabled={true}
          className={classnames(
            "ra-delete-button",
            classes.deleteButton,
            className,
          )}
          {...props}
        >
          <DeleteIcon />
        </Button>
      </span>
    </Tooltip>
  ) : (
    <>
      <Button
        {...props}
        label="Delete"
        disabled={isDeleting || isSaving}
        onClick={handleDialogOpen}
        className={classnames(
          "ra-delete-button",
          classes.deleteButton,
          className,
        )}
      >
        <DeleteIcon />
      </Button>
      <Confirm
        isOpen={open}
        loading={isDeleting}
        title="Delete Confirmation"
        content="Are you sure you want to delete this Inbound Shipment?"
        onConfirm={handleDelete}
        onClose={handleDialogClose}
      />
    </>
  );
};
